import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    ArrowLeft, Paperclip, Send, Image as ImageIcon, FileText,
    Download, Trash2, ShieldAlert, Eye, EyeOff, FileArchive, Pencil, Save,
    Phone, Mail, Users, Lock, StickyNote, Plus, ArrowDownLeft, ArrowUpRight,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { STATUS_META, SEVERITY_META, fmtDt, statusLabel, severityLabel } from "../lib/incidents";
import { t } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

/** Detail view for a single incident. Shared by admin + kunde – controls
 *  render conditionally based on role. */
export default function IncidentDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const lang = !isAdmin && user?.language === "en" ? "en" : "no";
    const [incident, setIncident] = useState(null);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");
    const [pendingAttachments, setPendingAttachments] = useState([]); // {file_id, filename, kind}
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const [insuranceDraft, setInsuranceDraft] = useState({ insurance_ref: "", insurance_company: "" });
    const [insuranceEditing, setInsuranceEditing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [logDialogOpen, setLogDialogOpen] = useState(false);
    const fileInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/incidents/${id}`);
            setIncident(data);
            setInsuranceDraft({
                insurance_ref: data.insurance_ref || "",
                insurance_company: data.insurance_company || "",
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke laste sak");
            setIncident(null);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [id]);

    const uploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        const next = [...pendingAttachments];
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/files/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const kind = (f.type || "").startsWith("image/") ? "photo" : "document";
                next.push({
                    file_id: data.id,
                    filename: data.original_filename || f.name,
                    kind,
                });
            }
            setPendingAttachments(next);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Opplasting feilet");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const sendMessage = async () => {
        if (!text.trim() && pendingAttachments.length === 0) return;
        setSending(true);
        try {
            await api.post(`/incidents/${id}/messages`, {
                text: text.trim() || (pendingAttachments.length ? "(vedlegg lagt til)" : ""),
                attachments: pendingAttachments,
            });
            setText("");
            setPendingAttachments([]);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke sende");
        } finally { setSending(false); }
    };

    const changeStatus = async (v) => {
        try {
            await api.patch(`/incidents/${id}`, { status: v });
            toast.success("Status oppdatert");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke endre status");
        }
    };

    const changeSeverity = async (v) => {
        try {
            await api.patch(`/incidents/${id}`, { severity: v });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Feil");
        }
    };

    const saveInsurance = async () => {
        try {
            await api.patch(`/incidents/${id}`, {
                insurance_ref: insuranceDraft.insurance_ref.trim(),
                insurance_company: insuranceDraft.insurance_company.trim(),
            });
            toast.success("Forsikringsinfo lagret");
            setInsuranceEditing(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Feil");
        }
    };

    const exportZip = async () => {
        setExporting(true);
        try {
            const r = await api.get(`/incidents/${id}/export.zip`, { responseType: "blob" });
            const url = window.URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${incident.reference_no || id}_skadesak.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("ZIP lastet ned");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Eksport feilet");
        } finally { setExporting(false); }
    };

    const download = async (att) => {
        try {
            const r = await api.get(`/files/${att.file_id}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.filename || att.file_id;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Nedlasting feilet");
        }
    };

    const removeAttachment = async (attId) => {
        if (!window.confirm("Slette dette vedlegget?")) return;
        try {
            await api.delete(`/incidents/${id}/attachments/${attId}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Feil");
        }
    };

    const toggleEntryVisibility = async (entry) => {
        const currentlyVisible = entry.visible_to_customer === true;
        const target = !currentlyVisible;
        try {
            await api.patch(`/incidents/${id}/log-entries/${entry.id}/visibility`, {
                visible_to_customer: target,
            });
            toast.success(target ? "Delt med kunden" : "Skjult for kunden");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke endre synlighet");
        }
    };

    if (loading) {
        return <div className="max-w-4xl mx-auto py-8 text-[#2B3F4D]">{t(lang, "incidents_loading")}</div>;
    }
    if (!incident) {
        return (
            <div className="max-w-4xl mx-auto py-8">
                <div className="text-red-600">{t(lang, "incident_not_found")}</div>
                <Button variant="outline" onClick={() => nav("/incidents")} className="mt-3">
                    <ArrowLeft className="w-4 h-4 mr-1.5" /> {t(lang, "incident_go_to_list")}
                </Button>
            </div>
        );
    }

    const st = STATUS_META[incident.status] || STATUS_META.open;
    const sev = SEVERITY_META[incident.severity] || SEVERITY_META.moderate;
    const canWrite = incident.status !== "closed";

    return (
        <div className="max-w-4xl mx-auto space-y-5 pb-16" data-testid="incident-detail">
            <Link
                to="/incidents"
                className="inline-flex items-center text-sm text-[#A57F39] hover:text-[#BD984D]"
            >
                <ArrowLeft className="w-4 h-4 mr-1" /> {t(lang, "incident_back_to_list")}
            </Link>

            <Card className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                            <ShieldAlert className="w-4 h-4 text-red-600" />
                            <span className="font-mono text-[#A57F39] font-semibold">
                                {incident.reference_no}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full border ${st.cls}`}>
                                {statusLabel(incident.status, lang)}
                            </span>
                            <span className={sev.cls}>· {severityLabel(incident.severity, lang)}</span>
                        </div>
                        <h1 className="text-2xl font-bold text-[#0C1824] mt-1">
                            {incident.title}
                        </h1>
                        <div className="text-sm text-[#2B3F4D] mt-1">
                            {isAdmin && <>Kunde: <b>{incident.customer_name}</b> · </>}
                            {t(lang, "incident_created")} {fmtDt(incident.created_at, lang)}
                            {incident.resolved_at && ` · ${t(lang, "incident_resolved_at")} ${fmtDt(incident.resolved_at, lang)}`}
                            {incident.closed_at && ` · ${t(lang, "incident_closed_at")} ${fmtDt(incident.closed_at, lang)}`}
                        </div>
                        {incident.description && (
                            <p className="text-sm text-[#0C1824] mt-3 whitespace-pre-wrap">
                                {incident.description}
                            </p>
                        )}
                    </div>

                    {isAdmin && (
                        <div className="grid gap-2" data-testid="incident-admin-controls">
                            <div>
                                <Label className="text-xs">Status</Label>
                                <Select value={incident.status} onValueChange={changeStatus}>
                                    <SelectTrigger className="w-[180px]" data-testid="incident-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(STATUS_META).map((k) => (
                                            <SelectItem key={k} value={k}>{STATUS_META[k].no}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Alvorlighet</Label>
                                <Select value={incident.severity} onValueChange={changeSeverity}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(SEVERITY_META).map((k) => (
                                            <SelectItem key={k} value={k}>{SEVERITY_META[k].no}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button
                                onClick={exportZip}
                                disabled={exporting}
                                variant="outline"
                                className="w-[180px] border-[#BD984D]/50 text-[#A57F39] hover:bg-[#BD984D]/10"
                                data-testid="incident-export-zip"
                            >
                                <FileArchive className="w-4 h-4 mr-1.5" />
                                {exporting ? "Pakker…" : "Last ned ZIP"}
                            </Button>
                        </div>
                    )}
                </div>

                {/* --- insurance reference (visible to both admin and customer) --- */}
                {(isAdmin || incident.insurance_ref || incident.insurance_company) && (
                    <div
                        className="mt-4 pt-4 border-t border-slate-100"
                        data-testid="incident-insurance-box"
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] font-semibold">
                                {t(lang, "incident_insurance_title")}
                            </div>
                            {isAdmin && !insuranceEditing && (
                                <button
                                    onClick={() => setInsuranceEditing(true)}
                                    className="text-xs text-[#A57F39] hover:text-[#BD984D] inline-flex items-center gap-1"
                                    data-testid="incident-insurance-edit"
                                >
                                    <Pencil className="w-3 h-3" /> Rediger
                                </button>
                            )}
                        </div>
                        {isAdmin && insuranceEditing ? (
                            <div className="mt-2 grid sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Selskap</Label>
                                    <Input
                                        value={insuranceDraft.insurance_company}
                                        onChange={(e) => setInsuranceDraft((s) => ({ ...s, insurance_company: e.target.value }))}
                                        placeholder="F.eks. Gjensidige"
                                        data-testid="incident-insurance-company"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Sak-/skade-nr.</Label>
                                    <Input
                                        value={insuranceDraft.insurance_ref}
                                        onChange={(e) => setInsuranceDraft((s) => ({ ...s, insurance_ref: e.target.value }))}
                                        placeholder="F.eks. 12345-2026-A"
                                        data-testid="incident-insurance-ref"
                                    />
                                </div>
                                <div className="sm:col-span-2 flex gap-2">
                                    <Button
                                        onClick={saveInsurance}
                                        className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                                        size="sm"
                                        data-testid="incident-insurance-save"
                                    >
                                        <Save className="w-4 h-4 mr-1.5" /> Lagre
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setInsuranceEditing(false);
                                            setInsuranceDraft({
                                                insurance_ref: incident.insurance_ref || "",
                                                insurance_company: incident.insurance_company || "",
                                            });
                                        }}
                                        variant="outline"
                                        size="sm"
                                    >
                                        Avbryt
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-2 text-sm text-[#0C1824] grid sm:grid-cols-2 gap-2">
                                <div>
                                    <span className="text-xs text-slate-500 block">{t(lang, "incident_insurance_company")}</span>
                                    {incident.insurance_company || <span className="italic text-slate-400">{t(lang, "incident_insurance_not_set")}</span>}
                                </div>
                                <div>
                                    <span className="text-xs text-slate-500 block">{t(lang, "incident_insurance_ref")}</span>
                                    {incident.insurance_ref
                                        ? <span className="font-mono">{incident.insurance_ref}</span>
                                        : <span className="italic text-slate-400">{t(lang, "incident_insurance_not_set")}</span>}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card>

            {/* -- attachments gallery -- */}
            {(incident.attachments || []).length > 0 && (
                <Card className="p-5" data-testid="incident-attachments">
                    <h2 className="font-semibold text-[#0C1824] mb-3 flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-[#A57F39]" /> {t(lang, "incident_attachments_title")} ({incident.attachments.length})
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {incident.attachments.map((att) => (
                            <AttachmentTile
                                key={att.id}
                                att={att}
                                isAdmin={isAdmin}
                                lang={lang}
                                onDownload={() => download(att)}
                                onRemove={() => removeAttachment(att.id)}
                            />
                        ))}
                    </div>
                </Card>
            )}

            {/* -- message thread -- */}
            <Card className="p-5" data-testid="incident-messages">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                    <h2 className="font-semibold text-[#0C1824]">
                        {isAdmin
                            ? `Meldinger & korrespondanse (${(incident.messages || []).length})`
                            : `${t(lang, "incident_messages_title")} (${(incident.messages || []).length})`}
                    </h2>
                    {isAdmin && canWrite && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setLogDialogOpen(true)}
                            className="border-[#BD984D]/50 text-[#A57F39] hover:bg-[#BD984D]/10"
                            data-testid="incident-add-log-entry"
                        >
                            <Plus className="w-4 h-4 mr-1.5" /> Legg til logg
                        </Button>
                    )}
                </div>
                {(incident.messages || []).length === 0 ? (
                    <p className="text-sm text-[#2B3F4D] italic">{t(lang, "incident_messages_empty")}</p>
                ) : (
                    <div className="space-y-4">
                        {[...incident.messages]
                            .sort((a, b) => {
                                const ka = a.occurred_at || a.created_at || "";
                                const kb = b.occurred_at || b.created_at || "";
                                return ka.localeCompare(kb);
                            })
                            .map((m) => (
                                <MessageBubble
                                    key={m.id}
                                    message={m}
                                    isOwn={m.author_user_id === user?.id}
                                    isAdmin={isAdmin}
                                    lang={lang}
                                    onDownloadAttachment={download}
                                    onToggleVisibility={toggleEntryVisibility}
                                />
                            ))}
                    </div>
                )}
            </Card>

            {/* -- composer -- */}
            {canWrite ? (
                <Card className="p-5" data-testid="incident-composer">
                    <Label>{isAdmin ? "Skriv en melding" : t(lang, "incident_composer_label")}</Label>
                    <Textarea
                        rows={3}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={isAdmin
                            ? "Skriv til kunden…"
                            : t(lang, "incident_composer_placeholder_customer")}
                        data-testid="incident-composer-text"
                    />
                    {pendingAttachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {pendingAttachments.map((a) => (
                                <span
                                    key={a.file_id}
                                    className="text-xs bg-slate-100 rounded px-2 py-1 flex items-center gap-1.5"
                                >
                                    {a.kind === "photo" ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                    {a.filename}
                                    <button
                                        onClick={() => setPendingAttachments((s) => s.filter((x) => x.file_id !== a.file_id))}
                                        className="text-red-500 hover:text-red-700 ml-1"
                                    >×</button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex gap-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                multiple
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(e) => uploadFiles(e.target.files)}
                                data-testid="incident-composer-file-input"
                            />
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading || sending}
                                data-testid="incident-composer-attach"
                            >
                                <Paperclip className="w-4 h-4 mr-1.5" />
                                {uploading
                                    ? (isAdmin ? "Laster opp…" : t(lang, "incident_composer_uploading"))
                                    : (isAdmin ? "Legg ved bilde/dokument" : t(lang, "incident_composer_attach"))}
                            </Button>
                        </div>
                        <Button
                            onClick={sendMessage}
                            disabled={sending || uploading || (!text.trim() && pendingAttachments.length === 0)}
                            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                            data-testid="incident-composer-send"
                        >
                            <Send className="w-4 h-4 mr-1.5" />
                            {sending
                                ? (isAdmin ? "Sender…" : t(lang, "incident_composer_sending"))
                                : (isAdmin ? "Send" : t(lang, "incident_composer_send"))}
                        </Button>
                    </div>
                </Card>
            ) : (
                <Card className="p-5 text-center text-sm text-[#2B3F4D]">
                    {isAdmin
                        ? "Denne saken er lukket. Ta kontakt med Canal Boats om noe må gjenåpnes."
                        : t(lang, "incident_closed_notice")}
                </Card>
            )}

            {/* -- admin-only history log -- */}
            {isAdmin && (incident.history || []).length > 0 && (
                <details className="text-sm">
                    <summary className="cursor-pointer text-[#A57F39] font-medium">
                        Intern historikk ({incident.history.length})
                    </summary>
                    <ul className="mt-2 space-y-1 text-xs text-[#2B3F4D]">
                        {incident.history.map((h, i) => (
                            <li key={i}>
                                <span className="font-mono text-slate-400">{fmtDt(h.at)}</span>{" "}
                                <b>{h.action}</b> — {h.by_user_name || "system"}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {isAdmin && (
                <AddLogEntryDialog
                    open={logDialogOpen}
                    onOpenChange={setLogDialogOpen}
                    incidentId={id}
                    onCreated={load}
                />
            )}
        </div>
    );
}

function AttachmentTile({ att, isAdmin, lang = "no", onDownload, onRemove }) {
    const isPhoto = (att.content_type || "").startsWith("image/") || att.kind === "photo";
    const [imgUrl, setImgUrl] = useState(null);

    useEffect(() => {
        if (!isPhoto) return;
        let revoked = false;
        api.get(`/files/${att.file_id}`, { responseType: "blob" })
            .then((r) => {
                if (revoked) return;
                setImgUrl(URL.createObjectURL(r.data));
            })
            .catch(() => setImgUrl(null));
        return () => { revoked = true; if (imgUrl) URL.revokeObjectURL(imgUrl); };
    }, [att.file_id, isPhoto]);

    return (
        <div
            className="rounded-lg border border-slate-200 overflow-hidden flex flex-col bg-white"
            data-testid={`incident-attachment-${att.id}`}
        >
            <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                {isPhoto && imgUrl ? (
                    <img src={imgUrl} alt={att.filename} className="w-full h-full object-cover" />
                ) : (
                    <FileText className="w-10 h-10 text-slate-400" />
                )}
            </div>
            <div className="p-2 flex-1 flex flex-col">
                <div className="text-xs font-medium text-[#0C1824] truncate" title={att.filename}>
                    {att.filename}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                    {att.uploaded_by_role === "kunde"
                        ? t(lang, "incident_attachment_from_customer")
                        : t(lang, "incident_attachment_from_company")}{" "}
                    · {fmtDt(att.created_at, lang)}
                </div>
                {isAdmin && !att.visible_to_customer && (
                    <div className="text-[10px] text-red-700 mt-0.5 inline-flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Kun intern
                    </div>
                )}
                <div className="mt-2 flex gap-1">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs px-2 flex-1"
                        onClick={onDownload}
                    >
                        <Download className="w-3 h-3 mr-1" /> {t(lang, "incident_attachment_download")}
                    </Button>
                    {isAdmin && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-red-600 hover:text-red-700"
                            onClick={onRemove}
                        >
                            <Trash2 className="w-3 h-3" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

function MessageBubble({ message, isOwn, onDownloadAttachment, isAdmin, lang = "no", onToggleVisibility }) {
    const fromAdmin = message.author_role !== "kunde";
    const kind = message.kind || "chat";
    const meta = LOG_KIND_META[kind] || LOG_KIND_META.chat;
    const Icon = meta.icon;
    const isLog = kind !== "chat";
    const hiddenFromCustomer = isLog && message.visible_to_customer === false;
    const kindLabel = ({
        phone_call: t(lang, "incident_log_kind_phone_call"),
        email: t(lang, "incident_log_kind_email"),
        meeting: t(lang, "incident_log_kind_meeting"),
        note: t(lang, "incident_log_kind_note"),
        internal_note: "Internt notat",
        chat: t(lang, "incident_log_kind_message"),
    })[kind] || meta.label;

    // Log-type entries get their own distinct style (kort/notatblokk)
    if (isLog) {
        return (
            <div className="w-full" data-testid={`incident-message-${message.id}`}>
                <div
                    className={`rounded-lg border-l-4 ${meta.border} ${meta.bg} px-4 py-3`}
                >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${meta.text}`}
                            >
                                <Icon className="w-3.5 h-3.5" /> {kindLabel}
                            </span>
                            {message.direction === "incoming" && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-slate-600">
                                    <ArrowDownLeft className="w-3 h-3" /> {t(lang, "incident_log_incoming")}
                                </span>
                            )}
                            {message.direction === "outgoing" && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-slate-600">
                                    <ArrowUpRight className="w-3 h-3" /> {t(lang, "incident_log_outgoing")}
                                </span>
                            )}
                            {isAdmin && message.party_name && (
                                <span className="text-xs text-[#0C1824] font-medium truncate">
                                    · {message.party_name}
                                </span>
                            )}
                            {isAdmin && (hiddenFromCustomer ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
                                    <EyeOff className="w-3 h-3" /> Kun intern
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                                    <Eye className="w-3 h-3" /> Delt med kunde
                                </span>
                            ))}
                        </div>
                        {isAdmin && kind !== "internal_note" && (
                            <button
                                onClick={() => onToggleVisibility?.(message)}
                                className="text-[10px] text-[#A57F39] hover:text-[#BD984D] underline"
                                data-testid={`incident-toggle-visibility-${message.id}`}
                            >
                                {hiddenFromCustomer ? "Del med kunde" : "Skjul for kunde"}
                            </button>
                        )}
                    </div>
                    {isAdmin && message.subject && (
                        <div className="mt-1 text-sm font-semibold text-[#0C1824]">
                            {message.subject}
                        </div>
                    )}
                    <div className="mt-1 whitespace-pre-wrap text-sm text-[#0C1824] leading-relaxed">
                        {message.text}
                    </div>
                    {(message.attachments || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {message.attachments.map((a) => (
                                <button
                                    key={a.id}
                                    onClick={() => onDownloadAttachment(a)}
                                    className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-white/80 hover:bg-white border border-slate-200"
                                >
                                    {a.kind === "photo" ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                    {a.filename}
                                    <Download className="w-3 h-3 ml-1" />
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="mt-1.5 text-[10px] text-slate-500">
                        {t(lang, "incident_log_logged_by")}{" "}
                        <b>{message.author_name || t(lang, "incident_customer_team")}</b>
                        {" · "}
                        {fmtDt(message.occurred_at || message.created_at, lang)}
                    </div>
                </div>
            </div>
        );
    }

    // Standard chat bubble
    return (
        <div
            className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
            data-testid={`incident-message-${message.id}`}
        >
            <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    isOwn
                        ? "bg-[#BD984D] text-white rounded-br-none"
                        : fromAdmin
                            ? "bg-slate-100 text-[#0C1824] rounded-bl-none"
                            : "bg-cyan-50 text-[#0C1824] border border-cyan-200 rounded-bl-none"
                }`}
            >
                <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1 ${
                    isOwn ? "text-white/80" : "text-slate-500"
                }`}>
                    {message.author_name
                        || (fromAdmin ? t(lang, "incident_customer_team") : t(lang, "incident_customer_you"))}
                    <span className="ml-2 opacity-70 normal-case tracking-normal">
                        {fmtDt(message.created_at, lang)}
                    </span>
                </div>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {message.text}
                </div>
                {(message.attachments || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {message.attachments.map((a) => (
                            <button
                                key={a.id}
                                onClick={() => onDownloadAttachment(a)}
                                className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded ${
                                    isOwn ? "bg-white/20 hover:bg-white/30" : "bg-white/70 hover:bg-white border border-slate-200"
                                }`}
                            >
                                {a.kind === "photo" ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                {a.filename}
                                <Download className="w-3 h-3 ml-1" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const LOG_KIND_META = {
    chat: { label: "Melding", icon: Send, border: "border-[#BD984D]", bg: "bg-[#BD984D]/5", text: "text-[#A57F39]" },
    phone_call: { label: "Telefon", icon: Phone, border: "border-amber-500", bg: "bg-amber-50", text: "text-amber-700" },
    email: { label: "E-post", icon: Mail, border: "border-sky-500", bg: "bg-sky-50", text: "text-sky-700" },
    meeting: { label: "Møte", icon: Users, border: "border-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700" },
    internal_note: { label: "Internt notat", icon: Lock, border: "border-slate-500", bg: "bg-slate-100", text: "text-slate-700" },
    note: { label: "Notat", icon: StickyNote, border: "border-[#0C6E7E]", bg: "bg-cyan-50", text: "text-[#0C6E7E]" },
};

function AddLogEntryDialog({ open, onOpenChange, incidentId, onCreated }) {
    const fileRef = useRef(null);
    const [form, setForm] = useState({
        kind: "phone_call",
        direction: "outgoing",
        party_name: "",
        subject: "",
        text: "",
        occurred_at: "",
        visible_to_customer: false,
    });
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            kind: "phone_call",
            direction: "outgoing",
            party_name: "",
            subject: "",
            text: "",
            occurred_at: new Date().toISOString().slice(0, 16),
            visible_to_customer: false,
        });
        setAttachments([]);
    }, [open]);

    const uploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        const next = [...attachments];
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/files/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const kind = (f.type || "").startsWith("image/") ? "photo" : "document";
                next.push({ file_id: data.id, filename: data.original_filename || f.name, kind });
            }
            setAttachments(next);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Opplasting feilet");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const save = async () => {
        if (!form.text.trim()) {
            toast.error("Skriv innhold i loggen");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                kind: form.kind,
                text: form.text.trim(),
                party_name: form.party_name.trim(),
                subject: form.subject.trim(),
                visible_to_customer: form.kind === "internal_note" ? false : !!form.visible_to_customer,
                attachments,
            };
            if (form.kind === "phone_call" || form.kind === "email") {
                payload.direction = form.direction;
            }
            if (form.occurred_at) {
                // input type=datetime-local => local, convert to ISO UTC
                payload.occurred_at = new Date(form.occurred_at).toISOString();
            }
            await api.post(`/incidents/${incidentId}/log-entries`, payload);
            toast.success("Loggført");
            onCreated?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
        } finally { setSaving(false); }
    };

    const showDirection = form.kind === "phone_call" || form.kind === "email";
    const showSubject = form.kind === "email" || form.kind === "meeting";
    const showParty = form.kind !== "internal_note";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg max-h-[85vh] overflow-y-auto"
                data-testid="log-entry-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Legg til korrespondanse / notat</DialogTitle>
                    <DialogDescription>
                        Logg en telefonsamtale, e-post, møte eller et internt notat i saken.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Type</Label>
                        <Select
                            value={form.kind}
                            onValueChange={(v) => setForm((s) => ({ ...s, kind: v }))}
                        >
                            <SelectTrigger data-testid="log-entry-kind">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="phone_call">Telefonsamtale</SelectItem>
                                <SelectItem value="email">E-post</SelectItem>
                                <SelectItem value="meeting">Møte</SelectItem>
                                <SelectItem value="note">Notat</SelectItem>
                                <SelectItem value="internal_note">Internt notat (skjult for kunde)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {showDirection && (
                        <div>
                            <Label>Retning</Label>
                            <Select
                                value={form.direction || "outgoing"}
                                onValueChange={(v) => setForm((s) => ({ ...s, direction: v }))}
                            >
                                <SelectTrigger data-testid="log-entry-direction">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="incoming">Innkommende (til oss)</SelectItem>
                                    <SelectItem value="outgoing">Utgående (fra oss)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {showParty && (
                        <div>
                            <Label>Motpart / navn</Label>
                            <Input
                                value={form.party_name}
                                onChange={(e) => setForm((s) => ({ ...s, party_name: e.target.value }))}
                                placeholder="F.eks. Tryg Forsikring, Ola Nordmann, Verksted AS"
                                data-testid="log-entry-party"
                            />
                        </div>
                    )}

                    {showSubject && (
                        <div>
                            <Label>Emne / tittel</Label>
                            <Input
                                value={form.subject}
                                onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
                                placeholder={form.kind === "email" ? "F.eks. Skademelding – SAK-2026-0001" : "F.eks. Befaring i havna"}
                                data-testid="log-entry-subject"
                            />
                        </div>
                    )}

                    <div>
                        <Label>Tidspunkt</Label>
                        <Input
                            type="datetime-local"
                            value={form.occurred_at}
                            onChange={(e) => setForm((s) => ({ ...s, occurred_at: e.target.value }))}
                            data-testid="log-entry-occurred-at"
                        />
                    </div>

                    <div>
                        <Label>Innhold / referat *</Label>
                        <Textarea
                            rows={5}
                            value={form.text}
                            onChange={(e) => setForm((s) => ({ ...s, text: e.target.value }))}
                            placeholder="Skriv referat, hva som ble sagt, avtalt, bekreftet…"
                            data-testid="log-entry-text"
                        />
                    </div>

                    <div>
                        <Label>Vedlegg (bilde/PDF – valgfritt)</Label>
                        <input
                            type="file"
                            ref={fileRef}
                            multiple
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={(e) => uploadFiles(e.target.files)}
                            data-testid="log-entry-file-input"
                        />
                        <div className="flex flex-wrap gap-2 mt-1">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                data-testid="log-entry-attach"
                            >
                                <Paperclip className="w-4 h-4 mr-1.5" />
                                {uploading ? "Laster opp…" : "Legg ved fil"}
                            </Button>
                            {attachments.map((a) => (
                                <span
                                    key={a.file_id}
                                    className="text-xs bg-slate-100 rounded px-2 py-1 flex items-center gap-1.5"
                                >
                                    {a.kind === "photo" ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                    {a.filename}
                                    <button
                                        type="button"
                                        onClick={() => setAttachments((s) => s.filter((x) => x.file_id !== a.file_id))}
                                        className="text-red-500 hover:text-red-700 ml-1"
                                    >×</button>
                                </span>
                            ))}
                        </div>
                    </div>

                    <div
                        className={`flex items-center justify-between rounded-md border px-3 py-2 ${
                            form.kind === "internal_note"
                                ? "bg-slate-100 border-slate-300 opacity-60"
                                : "bg-white border-slate-200"
                        }`}
                    >
                        <div className="pr-3">
                            <div className="text-sm font-medium text-[#0C1824]">
                                Del med kunde
                            </div>
                            <div className="text-xs text-[#2B3F4D]">
                                {form.kind === "internal_note"
                                    ? "Interne notat er alltid skjult for kunden."
                                    : "Skru på for å la kunden se denne oppføringen i sin portal."}
                            </div>
                        </div>
                        <Switch
                            checked={form.kind !== "internal_note" && form.visible_to_customer}
                            onCheckedChange={(v) => setForm((s) => ({ ...s, visible_to_customer: v }))}
                            disabled={form.kind === "internal_note"}
                            data-testid="log-entry-visible-to-customer"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
                    <Button
                        onClick={save}
                        disabled={saving || uploading || !form.text.trim()}
                        className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                        data-testid="log-entry-save"
                    >
                        {saving ? "Lagrer…" : "Loggfør"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
